import { useState, useEffect, useMemo, Suspense } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// import Icon from "@mui/material/Icon";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { CircularProgress } from "@mui/material";
import SoftBox from "components/SoftBox";

// import SoftBox from "components/SoftBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import routes from "routes";
import {
  useSoftUIController,
  setMiniSidenav,
  // setOpenConfigurator,
} from "context";
import brand from "assets/images/logo.png";
import { ProtectedRoute } from "ProtectedRoute";
import useNavRoutes from "nav.routes";
import i18n from "i18next"; // Assuming i18next is used for i18n
import { usePermissions } from "context/permissions-context/permissions.context";
import GoogleAnalytics from "./utils/googleAanalytics";
import { initHotjar } from "./utils/hotjar";

export default function App() {
  const navigate = useNavigate();
  const navRoutes = useNavRoutes();
  const [controller, dispatch] = useSoftUIController();
  // eslint-disable-next-line
  const { miniSidenav, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const location = useLocation();
  const { userPermissions } = usePermissions();

  // Initialize cache for RTL support
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Set the language direction based on current language (LTR for English/French, RTL for Arabic)
  useEffect(() => {
    const currentDirection = i18n.language === "ar" ? "rtl" : "ltr";
    document.body.setAttribute("dir", currentDirection);
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    initHotjar();
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // const handleConfiguratorOpen = () =>
  //   setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const tokenExpired = useMemo(() => {
    const token = localStorage.getItem("token");
    if (!token) return true;
    try {
      const { exp } = JSON.parse(atob(token.split(".")[1]));
      return Date.now() / 1000 > exp;
    } catch {
      return true;
    }
    // eslint-disable-next-line
  }, [location.pathname]); // Re-evaluate on navigation

  useEffect(() => {
    const token = localStorage.getItem("token");

    // Handle expired or missing token
    if (token && tokenExpired) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/home");
      return;
    }

    // Handle automatic logout at token expiry
    if (token && !tokenExpired) {
      try {
        const { exp } = JSON.parse(atob(token.split(".")[1]));
        const timeUntilExpiry = exp * 1000 - Date.now();
        const logoutTimer = setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/home");
        }, timeUntilExpiry);
        return () => clearTimeout(logoutTimer);
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/home");
      }
    }

    // Redirect after login based on permissions
    const selectedWarehouse = localStorage.getItem("selectedWarehouse");

    if (!tokenExpired && selectedWarehouse && (location.pathname === "/authentication/connexion" || location.pathname === "/home")) {
      if (userPermissions.includes("dashboard_access")) {
        navigate("/statistiques/statistiques-generales");
      } else if (userPermissions.length > 0) {
        navigate("/gestion-des-commandes/commandes-encours");
      }
    } else if (
      !token &&
      location.pathname !== "/authentication/connexion" &&
      location.pathname !== "/home" &&
      location.pathname !== "/privacy"
    ) {
      navigate("/home");
    }
    // eslint-disable-next-line
  }, [location.pathname, navigate, userPermissions, tokenExpired]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        // Check if the route requires protection based on token and permissions
        if (route.protected) {
          // Use the ProtectedRoute component and pass the required permission
          return (
            <Route
              key={route.key}
              element={
                <ProtectedRoute requiredPermission={route.requiredPermission} />
              }
            >
              <Route path={route.route} element={route.component} />
            </Route>
          );
        }
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  // const configsButton = (
  //   <SoftBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.5rem"
  //     height="3.5rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="default" color="inherit">
  //       settings
  //     </Icon>
  //   </SoftBox>
  // );

  return i18n.language === "ar" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <GoogleAnalytics />
        <CssBaseline />
        {layout === "dashboard" && pathname !== "/privacy" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={brand}
              brandName="Livrili Dashboard"
              routes={navRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {/* {configsButton} */}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Suspense
          fallback={
            <SoftBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="100vh"
            >
              <CircularProgress color="info" />
            </SoftBox>
          }
        >
          <Routes>
            {getRoutes(routes)}
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    tokenExpired
                      ? "/home"
                      : "/statistiques/statistiques-generales"
                  }
                />
              }
            />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <GoogleAnalytics />
      <CssBaseline />
      {layout === "dashboard" && pathname !== "/privacy" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Livrili Dashboard"
            routes={navRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Suspense
        fallback={
          <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
          >
            <CircularProgress color="info" />
          </SoftBox>
        }
      >
        <Routes>
          {getRoutes(routes)}
          <Route
            path="*"
            element={
              <Navigate
                to={
                  tokenExpired
                    ? "/home"
                    : "/statistiques/statistiques-generales"
                }
              />
            }
          />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
